/**
 * CONTAS DO EXTRATO DE VIAGEM — sem tela e sem armazenamento, para dar para
 * testar (`testes/contas.mjs`). Os tipos moram aqui pelo mesmo motivo; o
 * armazém do extrato (`src/dados/extrato.ts`) os reexporta.
 */
import type { Comprovante } from './tipos'

export type TipoDeLancamento = 'despesa' | 'adiantamento'
/** «gestor» = a empresa paga (entra no acerto); «comprador» = fica com ele (só registro) */
export type PorConta = 'gestor' | 'comprador'

export interface Anulacao { em: string; por?: string; motivo: string }

export interface Lancamento {
  id: string
  compradorId: string
  compradorNome: string
  tipo: TipoDeLancamento
  data: string
  valor: number
  categoria: string
  descricao: string
  porConta: PorConta
  /** id da foto no IndexedDB (e, depois da 2G, em `comprovantes`) */
  comprovanteId: string | null
  /** nome/tipo do arquivo: só existe no aparelho que tirou a foto */
  comprovante?: Comprovante
  lancadoPor: string
  lancadoPorNome: string
  lancadoEm: string
  anulacao: Anulacao | null
  acertoId: string | null
  /**
   * O servidor recusou de vez (ex.: o comprador saiu da equipe). Só existe
   * neste aparelho: fica riscado, fora das somas, com a frase do servidor —
   * nada se apaga, e a pessoa vê o que não entrou.
   */
  recusa?: { em: string; motivo: string } | null
}

export interface Acerto {
  id: string
  compradorId: string
  de: string
  ate: string
  despesas: number
  adiantamentos: number
  obs: string
  comprovanteId: string | null
  feitoPor: string
  feitoEm: string
  anulacao: Anulacao | null
  /** só chega ao aparelho de gestor e consulta; null = não calculada na época */
  comissoes?: number | null
  /**
   * R1A · saldo negativo do acerto anterior que entrou neste (só gestor e
   * Consulta: leva comissão dentro, e o comprador não vê comissão).
   */
  saldoAnterior?: number | null
  /** R1A · o pagamento do acerto (opcional): valor, data, quem lançou e o comprovante */
  valorPago?: number | null
  pagoEm?: string | null
  pagoPorNome?: string
  pagoComprovanteId?: string | null
}

/**
 * R1A · O saldo de um acerto como o gestor e a Consulta o veem:
 *   saldo anterior + despesas a ressarcir − vales + comissões.
 * Negativo: os vales passaram do que o comprador tinha a receber — e o
 * próximo acerto começa com ele (`saldoQuePassa`), em vez de sumir.
 */
export function saldoDoAcerto(a: Pick<Acerto, 'despesas' | 'adiantamentos' | 'comissoes' | 'saldoAnterior'>): number {
  return Math.round(((a.saldoAnterior ?? 0) + a.despesas - a.adiantamentos + (a.comissoes ?? 0)) * 100) / 100
}

/**
 * O saldo que passa para o próximo acerto deste comprador: o do último acerto
 * que vale, se ficou negativo. Positivo a empresa pagou: não passa. A mesma
 * conta do banco (013 saldo_anterior_do_acerto).
 */
export function saldoQuePassa(acertos: Acerto[], compradorId: string): number {
  const ultimo = acertos.filter((a) => a.compradorId === compradorId && !a.anulacao)
    .sort((a, b) => (b.ate === a.ate ? b.feitoEm.localeCompare(a.feitoEm) : b.ate.localeCompare(a.ate)))[0]
  return ultimo ? Math.min(0, saldoDoAcerto(ultimo)) : 0
}

/** R1A · Os totais que a tela mostrou no acerto e que o servidor confere. */
export interface TotaisDoAcerto { despesas: number; adiantamentos: number; comissoes: number; saldoAnterior: number }

/**
 * O que mudou entre o que a tela mostrou e o que se calcula agora (ao
 * centavo). Vazio = pode acertar. Cada item é uma frase para a pessoa conferir.
 */
export function valoresQueMudaram(mostrado: TotaisDoAcerto, agora: TotaisDoAcerto): string[] {
  const nomes: [keyof TotaisDoAcerto, string][] = [
    ['despesas', 'despesas a ressarcir'], ['adiantamentos', 'vales'], ['comissoes', 'comissões'], ['saldoAnterior', 'saldo anterior'],
  ]
  const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  return nomes.filter(([k]) => Math.round(mostrado[k] * 100) !== Math.round(agora[k] * 100))
    .map(([k, nome]) => `${nome}: a tela mostrava ${brl(mostrado[k])}, agora é ${brl(agora[k])}`)
}

/** Acertado = amarrado a um acerto que não foi anulado. */
export function estaAcertado(l: Pick<Lancamento, 'acertoId'>, acertos: Pick<Acerto, 'id' | 'anulacao'>[]): boolean {
  return !!l.acertoId && acertos.some((a) => a.id === l.acertoId && !a.anulacao)
}

/** Em aberto = vale para o próximo acerto: não anulado, não acertado, não recusado pelo servidor. */
export function emAberto(l: Lancamento, acertos: Pick<Acerto, 'id' | 'anulacao'>[]): boolean {
  return !l.anulacao && !l.recusa && !estaAcertado(l, acertos)
}

export interface Parcial {
  /** despesas que a empresa paga (o comprador recebe de volta) */
  aRessarcir: number
  /** despesas que ficam com o comprador: só registro, não entram no saldo */
  doComprador: number
  adiantamentos: number
  /** despesas a ressarcir − vales (sem comissão) */
  saldoSemComissao: number
  lancamentos: number
}

export interface Resumo extends Parcial {
  /** em aberto com data ANTES do período: lançado atrasado, ou que ficou de fora de um acerto */
  antes: Parcial
  /** o que o «Marcar acertado» fecha: o período + o de antes (tudo em aberto até o «até») */
  aAcertar: Parcial
}

function somar(lancs: Lancamento[]): Parcial {
  const r: Parcial = { aRessarcir: 0, doComprador: 0, adiantamentos: 0, saldoSemComissao: 0, lancamentos: 0 }
  for (const l of lancs) {
    r.lancamentos++
    if (l.tipo === 'adiantamento') r.adiantamentos += l.valor
    else if (l.porConta === 'gestor') r.aRessarcir += l.valor
    else r.doComprador += l.valor
  }
  const c = (n: number) => Math.round(n * 100) / 100
  r.aRessarcir = c(r.aRessarcir); r.doComprador = c(r.doComprador); r.adiantamentos = c(r.adiantamentos)
  r.saldoSemComissao = c(r.aRessarcir - r.adiantamentos)
  return r
}

/**
 * O que está EM ABERTO no período, mais o que está em aberto de ANTES dele.
 * `aAcertar` é exatamente o conjunto que o «Marcar acertado» do gestor fecha
 * (servidor/extrato.ts › acertar): tudo em aberto com data até o «até». O «de»
 * só escolhe o que a tela mostra como período — senão a despesa que sincroniza
 * depois do acerto do mês dela nunca seria acertada.
 *   saldo do acerto = comissões a receber + despesas a ressarcir − vales
 * A comissão entra só na tela do gestor/consulta, somada por fora.
 */
export function resumir(lancs: Lancamento[], acertos: Pick<Acerto, 'id' | 'anulacao'>[], de: string, ate: string): Resumo {
  const abertos = lancs.filter((l) => emAberto(l, acertos) && l.data <= ate)
  const periodo = somar(abertos.filter((l) => l.data >= de))
  return { ...periodo, antes: somar(abertos.filter((l) => l.data < de)), aAcertar: somar(abertos) }
}
